var _dec, _dec2, _class, _ToggleGroup;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import { omitProps, pickProps, getElementType, callRenderProp } from '@instructure/ui-react-utils';
import { IconButton } from '@instructure/ui-buttons';
import { Transition } from '@instructure/ui-motion';
import { Expandable } from '@instructure/ui-expandable';
import { isActiveElement } from '@instructure/ui-dom-utils';
import { Flex } from '@instructure/ui-flex';
import { View } from '@instructure/ui-view';
import { IconArrowOpenEndSolid, IconArrowOpenDownSolid } from '@instructure/ui-icons';
import { testable } from '@instructure/ui-testable';
import { allowedProps, propTypes } from './props';
import { withStyle } from '@instructure/emotion';
import generateStyle from './styles';
import generateComponentTheme from './theme';

/**
---
category: components
---
**/
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
let ToggleGroup = (_dec = withStyle(generateStyle, generateComponentTheme), _dec2 = testable(), _dec(_class = _dec2(_class = (_ToggleGroup = class ToggleGroup extends Component {
  constructor(...args) {
    super(...args);
    this.ref = null;
    this._button = null;
    this._shouldTransition = false;
    this.handleRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
    this.handleButtonRef = el => {
      this._button = el;
    };
  }
  get focused() {
    return isActiveElement(this._button);
  }
  focus() {
    var _this$_button;
    (_this$_button = this._button) === null || _this$_button === void 0 ? void 0 : _this$_button.focus();
  }
  componentDidMount() {
    this._shouldTransition = true;
  }
  componentDidUpdate() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props, this.state);
  }
  renderIcon(expanded) {
    const Icon = expanded ? this.props.iconExpanded : this.props.icon;
    return Icon ? callRenderProp(Icon) : null;
  }
  renderToggle(toggleProps, expanded) {
    const _this$props2 = this.props,
      toggleLabel = _this$props2.toggleLabel,
      size = _this$props2.size;
    let label;
    if (typeof toggleLabel === 'function') {
      label = toggleLabel(expanded);
    } else {
      label = toggleLabel;
    }
    const props = {
      ...toggleProps
    };
    return _jsx(IconButton, {
      ...props,
      withBackground: false,
      withBorder: false,
      size: size === 'large' ? 'medium' : 'small',
      elementRef: this.handleButtonRef,
      screenReaderLabel: label,
      children: this.renderIcon(expanded)
    });
  }
  renderDetails(detailsProps) {
    const styles = this.props.styles;
    return _jsx(View, {
      ...detailsProps,
      display: "block",
      borderWidth: "small none none none",
      borderColor: styles === null || styles === void 0 ? void 0 : styles.borderColor,
      children: this.props.transition && this._shouldTransition ? _jsx(Transition, {
        transitionOnMount: true,
        in: true,
        type: "fade",
        children: this.props.children
      }) : this.props.children
    });
  }
  render() {
    const Element = getElementType(ToggleGroup, this.props);
    const styles = this.props.styles;
    return _jsx(Expandable, {
      ...pickProps(this.props, Expandable.allowedProps),
      children: ({
        expanded,
        getToggleProps,
        getDetailsProps
      }) => {
        return _jsxs(View, {
          ...omitProps(this.props, ToggleGroup.allowedProps),
          borderWidth: this.props.border ? 'small' : 'none',
          as: Element,
          elementRef: this.handleRef,
          display: "block",
          borderRadius: "medium",
          background: "primary",
          borderColor: styles === null || styles === void 0 ? void 0 : styles.borderColor,
          children: [_jsxs(Flex, {
            padding: this.props.size === 'small' ? 'x-small' : 'small small small x-small',
            children: [_jsx(Flex.Item, {
              children: this.renderToggle(getToggleProps(), expanded)
            }), _jsx(Flex.Item, {
              shouldGrow: true,
              shouldShrink: true,
              padding: "0 0 0 x-small",
              children: this.props.summary
            })]
          }), expanded ? this.renderDetails(getDetailsProps()) : _jsx("span", {
            ...getDetailsProps()
          })]
        });
      }
    });
  }
}, _ToggleGroup.displayName = "ToggleGroup", _ToggleGroup.componentId = 'ToggleGroup', _ToggleGroup.allowedProps = allowedProps, _ToggleGroup.propTypes = propTypes, _ToggleGroup.defaultProps = {
  size: 'medium',
  icon: IconArrowOpenEndSolid,
  iconExpanded: IconArrowOpenDownSolid,
  defaultExpanded: false,
  transition: true,
  as: 'span',
  border: true
}, _ToggleGroup)) || _class) || _class);
export default ToggleGroup;
export { ToggleGroup };